import {useCallback, useEffect, useRef} from "react";

export function scrollToElementById(id, options = {}) {
    const el = window.document.getElementById(id);
    if (el) {
        scrollToElement(el, options);
    }
}

export function scrollToElementByRef(ref, options = {}) {
    if (ref.current) {
        scrollToElement(ref.current, options);
    }
}

export function scrollToElement(el, options = {}) {
    let height = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;

    let scrollContainer;
    let dialogScrollBody = document.getElementsByClassName('MuiDialog-scrollBody');
    if (dialogScrollBody.length === 1) {
        scrollContainer = dialogScrollBody[0];
    } else {
        scrollContainer = window;
    }

    scrollContainer.scroll({
        top: el.offsetTop - (options.centered ? Math.max((height - el.clientHeight) / 2, 0) : 20),
        behavior: 'smooth'
    });
}

export function useAutoscroller(containerRefProvider, options) {
    const autoScrollerRef = useRef(null);
    const containerRef = useRef(null);
    const memoizedOptions = useRef(options);

    useEffect(() => {
        containerRef.current = containerRefProvider();
    }, [containerRefProvider]);

    useEffect(() => {
        if (!containerRef.current) {
            return;
        }
        autoScrollerRef.current = new AutoScroller(containerRef.current, memoizedOptions.current);
        return () => {
            autoScrollerRef.current.destroy();
        };
    }, []);

    const scrollToBottom = useCallback(() => {
        autoScrollerRef.current?._scroll(false);
    }, []);

    return {
        scrollToBottom: scrollToBottom
    };
}

export class AutoScroller {
    constructor(container, options) {
        this._container = container;
        this.setPreScrollCheck(options?.preScrollCheck);
        this._setupScrollListener();
        this._setupScrollObserver();
    }

    setPreScrollCheck(fn) {
        this._preScrollCheck = fn ?? (() => true);
    }

    _setupScrollObserver() {
        this._observer = new MutationObserver(() => {
            this._scroll(true);
        });

        this._observer.observe(this._container, {
            childList: true,
            subtree: true
        });

        setTimeout(() => {
            this._scroll(false);
        }, 0);
    }

    _scroll(checkIfAtBottom) {
        if (this._isUserScrolling || !this._preScrollCheck() || (checkIfAtBottom && !this._scrollIsAtBottom())) {
            return;
        }
        if (this._container === document.body) {
            window.scrollTo(0, document.body.scrollHeight);
        } else {
            this._container.scrollTop = this._container.scrollHeight;
        }
    }

    _scrollIsAtBottom() {
        const position = this._container.scrollHeight
            - (this._container.scrollTop + this._container.clientHeight);
        return position <= 300;
    }

    _setupScrollListener() {
        this._isUserScrolling = false;
        this._scrollTimeout = null;

        this._scrollListener = () => {
            clearTimeout(this._scrollTimeout);
            this._isUserScrolling = true;

            this._scrollTimeout = setTimeout(() => {
                this._isUserScrolling = false;
            }, 150);
        };

        this._container.addEventListener('scroll', this._scrollListener);
    }

    destroy() {
        this._observer.disconnect();
        clearTimeout(this._scrollTimeout);
        this._container.removeEventListener('scroll', this._scrollListener);
    }
}